import { create } from 'zustand';
import commonMiddlewares from '@rs-core/store/middlewares';
import { shared } from '../contexts/utils/zustand-use-broadcast';
import { logWarn } from '@rs-core/utils/logger';

const CACHE_KEYS = {
	STUDY_SERIES: 'studySeries',
	STUDY_MEASUREMENTS: 'studyMeasurements',
	STUDY_KEY_IMAGES: 'studyKeyImages',
};

const initialState = {
	priorStudiesLoaded: false,
	studySeries: {},
	studyMeasurements: {},
	studyKeyImages: {},
};

/**
 * Zustand store for managing the caching and retrieval of study data in the loading manager.
 */
export const studyLogStore = create(
	shared(
		commonMiddlewares((set, get) => ({
			...initialState,

			/**
			 * Sets a study ID in the loading manager for a specific key.
			 * @param {Object} params - Parameters for setting the study ID.
			 * @param {string} params.studyInstanceUID - Unique identifier for the study instance.
			 * @param {string} params.managingOrganizationId - Unique identifier for the managing organization.
			 * @param {string} key - Key under which the study ID is stored.
			 * @param {boolean} [flag=true] - Flag to set for the study ID.
			 */
			setStudyId: ({ studyInstanceUID, managingOrganizationId }, key, flag = true) => {
				if (!studyInstanceUID || !managingOrganizationId) {
					logWarn(
						'studyLogStore-setStudyId',
						'Invalid parameters: studyInstanceUID or managingOrganizationId is missing.'
					);
					return;
				}

				const studyLogs = { ...get() };
				studyLogs[key][managingOrganizationId] = studyLogs[key][managingOrganizationId] || {};
				studyLogs[key][managingOrganizationId][studyInstanceUID] = flag;

				set({ ...studyLogs });
			},

			/**
			 * Checks if a study ID is set in the loading manager for a specific key.
			 * @param {Object} params - Parameters for checking the study ID.
			 * @param {string} params.studyInstanceUID - Unique identifier for the study instance.
			 * @param {string} params.managingOrganizationId - Unique identifier for the managing organization.
			 * @param {string} key - Key under which the study ID is stored.
			 * @returns {boolean} `true` if the study ID is set, otherwise `false`.
			 */
			isStudyIdSet: ({ studyInstanceUID, managingOrganizationId }, key) => {
				if (!studyInstanceUID || !managingOrganizationId) return false;

				const studyLogs = get();
				return studyLogs?.[key]?.[managingOrganizationId]?.[studyInstanceUID] || false;
			},

			/**
			 * Caches a study series in the loading manager.
			 * @param {Object} params - Parameters for caching the study series.
			 * @param {boolean} [flag=true] - Flag to set for the study series.
			 */
			cacheStudySeries: (params, flag = true) => get().setStudyId(params, CACHE_KEYS.STUDY_SERIES, flag),

			/**
			 * Caches study measurements in the loading manager.
			 * @param {Object} params - Parameters for caching the study measurements.
			 * @param {boolean} [flag=true] - Flag to set for the study measurements.
			 */
			cacheStudyMeasurements: (params, flag = true) =>
				get().setStudyId(params, CACHE_KEYS.STUDY_MEASUREMENTS, flag),

			/**
			 * Caches study key images in the loading manager.
			 * @param {Object} params - Parameters for caching the study key images.
			 * @param {boolean} [flag=true] - Flag to set for the study key images.
			 */
			cacheStudyKeyImages: (params, flag = true) => get().setStudyId(params, CACHE_KEYS.STUDY_KEY_IMAGES, flag),

			/**
			 * Checks if a study series is cached in the loading manager.
			 * @param {Object} params - Parameters for checking the study series.
			 * @returns {boolean} `true` if the study series is cached, otherwise `false`.
			 */
			isStudySeriesCached: params => get().isStudyIdSet(params, CACHE_KEYS.STUDY_SERIES),

			/**
			 * Checks if study measurements are cached in the loading manager.
			 * @param {Object} params - Parameters for checking the study measurements.
			 * @returns {boolean} `true` if the study measurements are cached, otherwise `false`.
			 */
			isStudyMeasurementsCached: params => get().isStudyIdSet(params, CACHE_KEYS.STUDY_MEASUREMENTS),

			/**
			 * Checks if study key images are cached in the loading manager.
			 * @param {Object} params - Parameters for checking the study key images.
			 * @returns {boolean} `true` if the study key images are cached, otherwise `false`.
			 */
			isStudyKeyImagesCached: params => get().isStudyIdSet(params, CACHE_KEYS.STUDY_KEY_IMAGES),

			resetStudyLogStore: () => {
				set({
					priorStudiesLoaded: false,
					studySeries: {},
					studyMeasurements: {},
					studyKeyImages: {},
				});
			},
		}))
	)
);
